import { useEffect, useRef } from 'react'

// Define the key inside the hook
const SCROLL_POSITION_KEY = 'categoryNavScrollPosition'

/**
 * Custom hook for persisting scroll position of the category navigation
 *
 * @returns ref object to attach to the scrollable element
 */
const useScrollPersistence = () => {
  const scrollContainerRef = useRef<HTMLElement | null>(null)

  // Load saved scroll position from localStorage
  useEffect(() => {
    const savedPosition = localStorage.getItem(SCROLL_POSITION_KEY)
    if (savedPosition && scrollContainerRef.current) {
      // Schedule restoration after the component has fully rendered
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          const parsedPosition = parseInt(savedPosition, 10)
          scrollContainerRef.current.scrollTop = Number.isNaN(parsedPosition) ? 0 : parsedPosition
        }
      })
    }
  }, [])

  // Save scroll position on scroll (throttled)
  useEffect(() => {
    let timeout: NodeJS.Timeout

    const handleScroll = () => {
      if (timeout) clearTimeout(timeout)

      timeout = setTimeout(() => {
        if (scrollContainerRef.current) {
          const position = scrollContainerRef.current.scrollTop
          localStorage.setItem(SCROLL_POSITION_KEY, position.toString())
        }
      }, 100) // Throttle to every 100ms
    }

    const scrollElement = scrollContainerRef.current
    if (scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (timeout) clearTimeout(timeout)
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  // Save scroll position before unmount
  useEffect(() => {
    return () => {
      if (scrollContainerRef.current) {
        const position = scrollContainerRef.current.scrollTop
        localStorage.setItem(SCROLL_POSITION_KEY, position.toString())
      }
    }
  }, [])

  return scrollContainerRef
}

export default useScrollPersistence
